import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

// SEO-optimized individual market page
export const Route = createFileRoute('/_layout/markets/$marketId')({
  // Parse and validate market ID
  params: {
    marketId: z.string(),
  },
  
  // Load market data for SSR
  loader: async ({ params }) => {
    console.log('Loading market detail for ID:', params.marketId)
    
    try {
      const [market, relatedMarkets] = await Promise.all([
        fetch(`https://skybit-api.botnet-599.workers.dev/markets/${params.marketId}`).then(r => r.json()),
        fetch(`https://skybit-api.botnet-599.workers.dev/markets/${params.marketId}/related`).then(r => r.json()).catch(() => ({ markets: [] }))
      ])
      
      return {
        market,
        relatedMarkets,
        // Pre-generate affiliate links
        affiliateLinks: {
          polymarket: market.platform === 'polymarket' ? `https://polymarket.com/markets/${market.slug}?ref=skybit` : null,
          kalshi: market.platform === 'kalshi' ? `https://kalshi.com/markets/${market.ticker}?ref=skybit` : null,
        }
      }
    } catch (error) {
      console.error('Error loading market:', error)
      return {
        market: null,
        relatedMarkets: { markets: [] },
        affiliateLinks: {}
      }
    }
  },
  
  // SEO meta tags
  meta: ({ loaderData }) => {
    if (!loaderData?.market) {
      return [
        { title: 'Market Not Found | Skybit' },
        { name: 'description', content: 'Market not found' }
      ]
    }
    return [
      { title: `${loaderData.market.question} - Live Odds | Skybit` },
      { name: 'description', content: `Current odds: YES ${(loaderData.market.yes_price * 100).toFixed(0)}% | NO ${(loaderData.market.no_price * 100).toFixed(0)}% | Volume: $${loaderData.market.volume?.toLocaleString() || '0'}` },
      { property: 'og:title', content: loaderData.market.question },
      { property: 'og:description', content: `Live prediction market odds and trading opportunities` },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ]
  },
  
  component: MarketPage,
})

function MarketPage() {
  console.log('MarketPage component rendered!')
  const { market, relatedMarkets, affiliateLinks } = Route.useLoaderData()
  
  return (
    <div className="market-page" style={{ padding: '2rem' }}>
      <h1>Market Detail Page</h1>
      <p>Market ID: {market?.id || 'Loading...'}</p>
      {/* Structured data for Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FinancialProduct",
          "name": market.question,
          "description": `Prediction market for: ${market.question}`,
          "offers": {
            "@type": "Offer",
            "price": market.yes_price,
            "priceCurrency": "USD"
          }
        })}
      </script>
      
      <h1>{market.question}</h1>
      
      {/* Prominent affiliate CTA */}
      <div className="trade-cta">
        <a 
          href={affiliateLinks[market.platform]} 
          target="_blank"
          rel="noopener"
          className="trade-button"
          onClick={() => {
            // Track conversion
            gtag('event', 'click', {
              event_category: 'affiliate',
              event_label: market.platform,
              value: market.volume
            })
          }}
        >
          Trade on {market.platform} →
        </a>
      </div>
      
      {/* Market data for SEO */}
      <div className="market-stats">
        <div>YES: {(market.yes_price * 100).toFixed(0)}%</div>
        <div>NO: {(market.no_price * 100).toFixed(0)}%</div>
        <div>Volume: ${market.volume.toLocaleString()}</div>
      </div>
      
      {/* Related markets for internal linking */}
      <section>
        <h2>Related Markets</h2>
        {relatedMarkets.map(m => (
          <a key={m.id} href={`/markets/${m.id}`}>
            {m.question}
          </a>
        ))}
      </section>
    </div>
  )
}